import React, { useState, useRef } from 'react';
import api from '../utils/api';
import toast from 'react-hot-toast';
import { Upload, FileJson, CheckCircle, AlertCircle, X } from 'lucide-react';

export default function ImportModal({ projectId, onClose, onImported }) {
  const [mode, setMode] = useState('file'); // file | url | paste
  const [url, setUrl] = useState('');
  const [text, setText] = useState('');
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [dragOver, setDragOver] = useState(false);
  const fileRef = useRef();

  async function importSpec(spec) {
    setLoading(true);
    try {
      const res = await api.post(`/projects/${projectId}/import/openapi`, spec);
      setResult(res.data);
      toast.success(`Imported ${res.data.created} endpoints!`);
      onImported && onImported();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Import failed');
    } finally { setLoading(false); }
  }

  async function handleFile(file) {
    if (!file) return;
    try {
      const text = await file.text();
      const spec = JSON.parse(text);
      await importSpec(spec);
    } catch { toast.error('Invalid JSON file'); }
  }

  async function handleUrl() {
    if (!url.trim()) return;
    setLoading(true);
    try {
      const res = await fetch(url);
      const spec = await res.json();
      await importSpec(spec);
    } catch { toast.error('Failed to fetch spec from URL'); setLoading(false); }
  }

  async function handlePaste() {
    try {
      const spec = JSON.parse(text);
      await importSpec(spec);
    } catch { toast.error('Invalid JSON'); }
  }

  function handleDrop(e) {
    e.preventDefault();
    setDragOver(false);
    const file = e.dataTransfer.files[0];
    if (file) handleFile(file);
  }

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal" style={{ maxWidth: 560, width: '95%' }} onClick={e => e.stopPropagation()}>
        <div style={sty.header}>
          <h3 className="modal-title" style={{ margin: 0 }}>Import OpenAPI Specification</h3>
          <button style={sty.closeBtn} onClick={onClose}><X size={16} /></button>
        </div>

        {!result ? (
          <>
            {/* Mode tabs */}
            <div className="tabs" style={{ marginBottom: 0 }}>
              {['file', 'url', 'paste'].map(m => (
                <button key={m} className={`tab ${mode === m ? 'active' : ''}`} onClick={() => setMode(m)}>
                  {m === 'file' ? '📁 File' : m === 'url' ? '🔗 URL' : '📋 Paste JSON'}
                </button>
              ))}
            </div>

            <div style={{ padding: '20px 0 0' }}>
              {mode === 'file' && (
                <div
                  style={{ ...sty.dropZone, ...(dragOver ? sty.dropZoneActive : {}) }}
                  onDragOver={e => { e.preventDefault(); setDragOver(true); }}
                  onDragLeave={() => setDragOver(false)}
                  onDrop={handleDrop}
                  onClick={() => fileRef.current?.click()}
                >
                  <FileJson size={32} style={{ color: dragOver ? 'var(--accent)' : 'var(--text-muted)', marginBottom: 10 }} />
                  <p style={{ color: 'var(--text-secondary)', marginBottom: 6 }}>
                    {dragOver ? 'Drop file here' : 'Click or drag & drop your OpenAPI 3.0 JSON file'}
                  </p>
                  <p style={{ fontSize: 11, color: 'var(--text-muted)' }}>Supports OpenAPI 3.0 JSON format</p>
                  <input ref={fileRef} type="file" accept=".json" style={{ display: 'none' }}
                    onChange={e => handleFile(e.target.files[0])} />
                </div>
              )}

              {mode === 'url' && (
                <div style={{ display: 'flex', gap: 8 }}>
                  <input value={url} onChange={e => setUrl(e.target.value)} placeholder="https://api.example.com/openapi.json"
                    onKeyDown={e => e.key === 'Enter' && handleUrl()} />
                  <button className="btn btn-primary" style={{ flexShrink: 0 }} onClick={handleUrl} disabled={loading}>
                    {loading ? 'Loading...' : 'Import'}
                  </button>
                </div>
              )}

              {mode === 'paste' && (
                <>
                  <textarea
                    style={sty.pasteArea}
                    className="mono"
                    value={text}
                    onChange={e => setText(e.target.value)}
                    placeholder={'{\n  "openapi": "3.0.0",\n  "info": { ... },\n  "paths": { ... }\n}'}
                    rows={10}
                  />
                  <button className="btn btn-primary" style={{ marginTop: 12, width: '100%' }} onClick={handlePaste} disabled={loading || !text.trim()}>
                    {loading ? 'Importing...' : 'Import Specification'}
                  </button>
                </>
              )}
            </div>

            <div style={sty.info}>
              <AlertCircle size={13} style={{ flexShrink: 0, color: 'var(--accent-orange)' }} />
              <span>Existing endpoints with the same method + path will be skipped to prevent duplicates.</span>
            </div>
          </>
        ) : (
          /* Success view */
          <div style={sty.resultView}>
            <CheckCircle size={48} style={{ color: 'var(--accent-green)' }} />
            <h3 style={sty.resultTitle}>Import Complete!</h3>
            <div style={sty.resultStats}>
              <div style={sty.resultStat}>
                <span style={{ ...sty.resultNum, color: 'var(--accent-green)' }}>{result.created}</span>
                <span style={sty.resultLabel}>Endpoints Created</span>
              </div>
              <div style={sty.resultStat}>
                <span style={{ ...sty.resultNum, color: 'var(--text-muted)' }}>{result.skipped}</span>
                <span style={sty.resultLabel}>Skipped (existing)</span>
              </div>
            </div>
            <button className="btn btn-primary" style={{ width: '100%', marginTop: 8 }} onClick={onClose}>Done</button>
          </div>
        )}
      </div>
    </div>
  );
}

const sty = {
  header: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 16 },
  closeBtn: { background: 'none', border: 'none', color: 'var(--text-muted)', cursor: 'pointer', padding: 4, display: 'flex', borderRadius: 5 },
  dropZone: { border: '2px dashed var(--border-light)', borderRadius: 10, padding: '36px 20px', textAlign: 'center', cursor: 'pointer', transition: 'all 0.15s', display: 'flex', flexDirection: 'column', alignItems: 'center' },
  dropZoneActive: { borderColor: 'var(--accent)', background: 'var(--accent-dim)' },
  pasteArea: { width: '100%', background: 'var(--bg-primary)', border: '1px solid var(--border)', borderRadius: 6, color: '#a8d8a8', fontSize: 12, padding: 12, resize: 'vertical', lineHeight: 1.6, fontFamily: 'var(--mono)' },
  info: { display: 'flex', alignItems: 'flex-start', gap: 8, marginTop: 16, padding: '10px 14px', background: 'rgba(245,166,35,0.08)', border: '1px solid rgba(245,166,35,0.2)', borderRadius: 7, fontSize: 12, color: 'var(--text-secondary)' },
  resultView: { display: 'flex', flexDirection: 'column', alignItems: 'center', padding: '20px 0', gap: 12 },
  resultTitle: { fontSize: 20, fontWeight: 700 },
  resultStats: { display: 'flex', gap: 32 },
  resultStat: { display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 4 },
  resultNum: { fontSize: 36, fontWeight: 800, lineHeight: 1 },
  resultLabel: { fontSize: 12, color: 'var(--text-muted)' },
};
